import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "../utils/apiClient";
import LoadingSpinner from "./LoadingSpinner";

const TailorRequirementsView = ({ ref, ...props }) => {
  const { onScoresChange } = props;
  const [aiFocus, setAiFocus] = useState(50);
  const [hotelType, setHotelType] = useState();
  const [location, setLocation] = useState(null);
  const [strategyScores, setStrategyScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [llmOutput, setLlmOutput] = useState("");
  const strategies = useMemo(
    () => [
      {
        name: "Technical SEO",
        baseValue: 6,
        llmWeight: 0.1,
        googleWeight: 0.9,
        icon: "🔧",
      },
      {
        name: "Content Hubs",
        baseValue: 7,
        llmWeight: 0.9,
        googleWeight: 0.1,
        icon: "📚",
      },
      {
        name: "Schema Markup",
        baseValue: 6,
        llmWeight: 0.4,
        googleWeight: 0.6,
        icon: "💻",
      },
      {
        name: "Digital PR",
        baseValue: 4,
        llmWeight: 0.8,
        googleWeight: 0.2,
        icon: "📢",
      },
      {
        name: "Review Management",
        baseValue: 8,
        llmWeight: 0.3,
        googleWeight: 0.7,
        icon: "⭐",
      },
      {
        name: "Local Links",
        baseValue: 6,
        llmWeight: 0.4,
        googleWeight: 0.6,
        icon: "🔗",
      },
      {
        name: "Amenity Content",
        baseValue: 4,
        llmWeight: 0.6,
        googleWeight: 0.4,
        icon: "💎",
      },
      {
        name: "UGC",
        baseValue: 4,
        llmWeight: 0.7,
        googleWeight: 0.3,
        icon: "📸",
      },
    ],
    [],
  );
  const hotelTypeWeights = useMemo(
    () => ({
      luxury: {
        "Digital PR": 1.2,
        "Content Hubs": 1.1,
        "Review Management": 1.1,
        "Amenity Content": 1.2,
      },
      budget: {
        "Technical SEO": 1.2,
        "Review Management": 1.1,
        "Local Links": 1.2,
        "Amenity Content": 0.8,
      },
      resort: {
        "Amenity Content": 1.5,
        "Content Hubs": 1.2,
        "Digital PR": 1.1,
        "Schema Markup": 1.1,
      },
      boutique: {
        "Content Hubs": 1.3,
        "Digital PR": 1.4,
        "Review Management": 1.2,
        UGC: 1.3,
      },
    }),
    [],
  );
  const calculateStrategyScores = useCallback(() => {
    const newScores = {};
    const llmFactor = (100 - aiFocus) / 100;
    const googleFactor = aiFocus / 100;
    strategies.forEach((strategy) => {
      let score =
        strategy.baseValue +
        strategy.llmWeight * llmFactor * 4 +
        strategy.googleWeight * googleFactor * 4;
      const weights = hotelTypeWeights[hotelType] || {};
      score = score * (weights[strategy.name] || 1);
      score = Math.max(0, Math.min(10, score));
      newScores[strategy.name] = score;
    });
    setStrategyScores(newScores);
    if (onScoresChange) {
      onScoresChange(newScores);
    }
  }, [aiFocus, hotelType, onScoresChange, strategies, hotelTypeWeights]);
  const generateLlmStrategy = useCallback(async () => {
    setLoading(true);
    setLlmOutput("");
    const scores = strategies
      .map((s) => `${s.name}: ${strategyScores[s.name]?.toFixed(1)}`)
      .join(", ");
    const prompt = `Act as a creative marketing strategist. Based on the following user inputs, provide a playful, innovative, and actionable GEO strategy for a hotel.
        - Hotel Type: ${hotelType}
        - Location: ${location}
        - Strategic Focus: ${aiFocus}% Google AI, ${100 - aiFocus}% LLM/ChatGPT
        - Calculated Strategy Scores (1-10): ${scores}
        Focus on creative and out-of-box ideas for the top 3-4 strategies.
        For "Content Hubs", suggest catchy blog series titles.
        For "Digital PR", propose a viral campaign idea.
        Make the response inspiring, fun, and professional. Use markdown for formatting.`;
    try {
      const response = await api.post("/generate-strategy", { prompt: prompt });
      const result = response.data;
      const text = result.strategy;
      setLlmOutput(text || "Could not generate a detailed strategy. Please try again.");
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to generate strategy. Server error.";
      setLlmOutput(`An error occurred: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [aiFocus, hotelType, location, strategyScores, strategies]);
  useEffect(() => {
    calculateStrategyScores();
  }, [calculateStrategyScores]);
  // Strategy generation is now triggered manually via button click only
  const RadarChart = ({ data }) => {
    const size = 380;
    const center = size / 2;
    const numPoints = strategies.length;
    const angle = 360 / numPoints;
    const maxScore = 10;
    const padding = 50;
    const radius = center - padding;
    const getPointCoordinates = (value, index, max) => {
      const currentAngle = angle * index;
      const x = center + radius * (value / max) * Math.cos(((currentAngle - 90) * Math.PI) / 180);
      const y = center + radius * (value / max) * Math.sin(((currentAngle - 90) * Math.PI) / 180);
      return { x, y };
    };
    const points = strategies
      .map((s, i) => {
        const score = data[s.name] || 0;
        const coords = getPointCoordinates(score, i, maxScore);
        return `${coords.x},${coords.y}`;
      })
      .join(" ");
    return (
      <div
        className="flex justify-center items-center w-full h-full relative"
        style={{ minHeight: `${size}px` }}
      >
        <svg width={size} height={size} className="overflow-visible">
          <defs>
            <radialGradient id="polygonGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(0, 64, 224, 0.5)" />
              <stop offset="100%" stopColor="rgba(46, 91, 255, 0.7)" />
            </radialGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {[2, 4, 6, 8, 10].map((score, i) => (
            <circle
              key={`c-${i}`}
              cx={center}
              cy={center}
              r={radius * (score / maxScore)}
              className="stroke-surface-container-high fill-transparent"
            />
          ))}
          {strategies.map((s, i) => {
            const endCoords = getPointCoordinates(maxScore, i, maxScore);
            const labelCoords = getPointCoordinates(maxScore + 4, i, maxScore);
            return (
              <g key={`axis-${i}`}>
                <line
                  x1={center}
                  y1={center}
                  x2={endCoords.x}
                  y2={endCoords.y}
                  className="stroke-outline-variant stroke-1"
                />
                <text
                  x={labelCoords.x}
                  y={labelCoords.y}
                  fill="#131b2e"
                  fontSize="13"
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="font-sans font-semibold"
                >
                  {s.icon} {s.name}
                </text>
              </g>
            );
          })}
          <polygon
            points={points}
            fill="url(#polygonGradient)"
            stroke="#0040e0"
            strokeWidth="2"
            style={{ transition: "points 0.5s ease-in-out" }}
            filter="url(#glow)"
          />
        </svg>
      </div>
    );
  };
  return (
    <div ref={ref} className="font-sans flex items-center justify-center">
      <div className="max-w-6xl mx-auto w-full">
        <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-cloud">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-on-surface mb-2">
              Hotel GEO Shaper
            </h1>
            <p className="text-on-surface-variant">
              Craft your next-gen marketing strategy with a sprinkle of AI magic ✨
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="bg-surface-container-low p-6 rounded-xl flex flex-col space-y-6 flex-1 min-h-[500px]">
              <div>
                <label className="block text-sm font-semibold text-on-surface-variant mb-2">
                  AI Engine Mix
                </label>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-primary font-semibold">LLM SEO</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={aiFocus}
                    onChange={(e) => {
                      setAiFocus(Number(e.target.value));
                    }}
                    className="w-full h-2 bg-surface-container-high rounded-lg appearance-none cursor-pointer range-slider"
                  />
                  <span className="text-sm text-primary font-semibold">SEO</span>
                </div>
                <div className="text-center mt-2 text-on-surface font-bold">
                  {aiFocus}% Google AI / {100 - aiFocus}% LLM
                </div>
              </div>
              <div>
                <label
                  htmlFor="hotelType"
                  className="block text-sm font-semibold text-on-surface-variant mb-2"
                >
                  Hotel Vibe
                </label>
                <select
                  id="hotelType"
                  value={hotelType}
                  onChange={(e) => {
                    setHotelType(e.target.value);
                  }}
                  className="w-full bg-surface-container-low text-on-surface rounded-xl p-3 border-none focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                >
                  <option value={null}>Select Hotel Type</option>
                  <option value="luxury">💎 Luxury</option>
                  <option value="boutique">🎨 Boutique</option>
                  <option value="resort">🌴 Resort</option>
                  <option value="budget">💰 Budget</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="location"
                  className="block text-sm font-semibold text-on-surface-variant mb-2"
                >
                  Target Playground
                </label>
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => {
                    setLocation(e.target.value);
                  }}
                  placeholder="e.g., 'Tokyo, Japan'"
                  className="w-full bg-surface-container-low text-on-surface rounded-xl p-3 border-none focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                />
              </div>
              <div className="pt-4">
                <button
                  onClick={generateLlmStrategy}
                  disabled={loading || !hotelType || !location}
                  className={`w-full py-3 rounded-lg font-bold text-white transition-all duration-300 transform hover:scale-105 ${loading || !hotelType || !location ? "bg-surface-container-high text-on-surface-variant cursor-not-allowed" : "bg-gradient-to-br from-primary to-primary-container hover:opacity-90 shadow-lg shadow-primary/20"}`}
                >
                  {loading ? "Conjuring Ideas..." : "Generate Strategy"}
                </button>
              </div>
            </div>
            <div className="bg-surface-container-low p-6 rounded-xl flex flex-col flex-1 min-h-[500px]">
              <h2 className="text-xl font-bold text-on-surface text-center mb-4">
                Strategy Priority Matrix
              </h2>
              <RadarChart data={strategyScores} />
            </div>
          </div>
          <div className="mt-8 bg-surface-container-low p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-on-surface mb-4 text-center">
              Your Custom Blueprint
            </h2>
            {loading ? (
              <div className="flex flex-col items-center justify-center p-8 text-primary">
                <LoadingSpinner size="md" message="Generating your custom strategy..." />
              </div>
            ) : (
              <div className="text-on-surface-variant leading-relaxed whitespace-pre-wrap prose prose-p:text-on-surface-variant prose-headings:text-on-surface prose-strong:text-primary">
                {llmOutput ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: llmOutput.replace(/[#*_-]/g, "").replace(/\n/g, "<br />"),
                    }}
                  />
                ) : (
                  "Adjust the controls and hit 'Generate Strategy' to cook up a personalized plan."
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
            .range-slider::-webkit-slider-thumb {-webkit-appearance: none; appearance: none; width: 20px; height: 20px; background: #0040e0; border-radius: 50%; cursor: pointer; border: 3px solid #ffffff; transition: background 0.3s ease-in-out;}
            .range-slider:hover::-webkit-slider-thumb {background: #0035b8;}`}</style>
    </div>
  );
};

export default TailorRequirementsView;
